import { useEffect } from 'react'
import { useForm, useStore } from "@tanstack/react-form";
import { z } from "zod";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PriceInput } from "@/components/ui/price-input";
import { Loader2 } from "lucide-react";
import { useBoats } from "@/features/inventory/hooks";
import { useCustomers } from "@/features/customers/hooks";
import { TechnicianSelect, TECHNICIAN_UNASSIGNED } from "@/features/service/technician-select";
import type { WorkOrderWriteBody } from "@/types/service";

export const workOrderFormSchema = z.object({
  boat_id: z.string().uuid("Select a boat"),
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  priority: z.enum(["low", "medium", "high", "urgent"]),
  assigned_technician: z.string().optional(),
  scheduled_date: z.string().optional(),
  due_date: z.string().optional(),
  parts_cost: z.string().optional(),
  labor_hours: z.string().optional(),
  labor_rate: z.string().optional(),
  customer_id: z.string().optional(),
  billable_labor_rate: z.string().optional(),
});

export type WorkOrderFormValues = z.infer<typeof workOrderFormSchema>;

interface WorkOrderFormProps {
  /** Reports dirty state up so the host dialog can warn before closing. */
  onDirtyChange?: (dirty: boolean) => void
  defaultValues?: Partial<WorkOrderFormValues>;
  onSubmit: (values: WorkOrderWriteBody) => void;
  isPending?: boolean;
  submitLabel?: string;
  preselectedBoatId?: string;
}

export function WorkOrderForm({ onDirtyChange,
  defaultValues,
  onSubmit,
  isPending,
  submitLabel = "Create Work Order",
  preselectedBoatId,
}: WorkOrderFormProps) {
  const { data: boats } = useBoats();
  const { data: customers } = useCustomers();

  const initialValues: WorkOrderFormValues = {
    boat_id: preselectedBoatId ?? "",
    title: "",
    description: "",
    priority: "medium",
    assigned_technician: "",
    scheduled_date: "",
    due_date: "",
    parts_cost: "",
    labor_hours: "",
    labor_rate: "",
    customer_id: "",
    billable_labor_rate: "",
    ...defaultValues,
  };

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: ({ value }) => {
      const num = (s: string | undefined) => (s && Number(s) > 0 ? Number(s) : undefined);
      onSubmit({
        ...value,
        description: value.description || undefined,
        assigned_technician:
          !value.assigned_technician || value.assigned_technician === TECHNICIAN_UNASSIGNED
            ? undefined
            : value.assigned_technician,
        scheduled_date: value.scheduled_date || undefined,
        due_date: value.due_date || undefined,
        parts_cost: num(value.parts_cost),
        labor_hours: num(value.labor_hours),
        labor_rate: num(value.labor_rate),
        customer_id:
          !value.customer_id || value.customer_id === "none"
            ? undefined
            : value.customer_id,
        billable_labor_rate: num(value.billable_labor_rate),
      });
    },
  });

  const isDirty = useStore(form.store, (state) => state.isDirty)
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange]);

  return (
    <Form form={form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        className="space-y-4"
      >
        <FormField
          name="boat_id"
          validators={{ onChange: workOrderFormSchema.shape.boat_id }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Boat</FormLabel>
              <Select onValueChange={field.onChange} value={field.value as string}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a boat" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {boats?.map((boat) => (
                    <SelectItem key={boat.id} value={boat.id}>
                      {boat.make} {boat.model} ({boat.year})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="title"
          validators={{ onChange: workOrderFormSchema.shape.title }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Engine oil change" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  placeholder="Details about the service task…"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            name="priority"
            validators={{ onChange: workOrderFormSchema.shape.priority }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Priority</FormLabel>
                <Select onValueChange={field.onChange} value={field.value as string}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="assigned_technician"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Technician</FormLabel>
                <FormControl>
                  <TechnicianSelect
                    value={(field.value as string) || ""}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            name="scheduled_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Scheduled Date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="due_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Due Date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            name="customer_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bill to customer</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={(field.value as string) || "none"}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="No payer (costs only)" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">No payer (costs only)</SelectItem>
                    {(customers ?? []).map((c) => {
                      const name =
                        [c.first_name, c.last_name].filter(Boolean).join(" ") ||
                        c.company_name ||
                        c.id;
                      return (
                        <SelectItem key={c.id} value={c.id}>
                          {name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <FormMessage />
                <p className="text-xs text-muted-foreground">
                  Completion generates an invoice (Finance → Invoices).
                </p>
              </FormItem>
            )}
          />

          <FormField
            name="billable_labor_rate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billable labor rate ($/hr)</FormLabel>
                <FormControl>
                  <PriceInput
                    placeholder="Falls back to internal rate"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <FormField
            name="parts_cost"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Parts cost ($)</FormLabel>
                <FormControl>
                  <PriceInput
                    placeholder="0"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="labor_hours"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Labor hours</FormLabel>
                <FormControl>
                  <Input type="number" min="0" step="0.5" placeholder="0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="labor_rate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Labor rate ($/hr)</FormLabel>
                <FormControl>
                  <PriceInput
                    placeholder="0"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" disabled={isPending} className="w-full">
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </form>
    </Form>
  );
}
